/**
 * 応用サンプル集（クックブック）。
 * 複数のコンポーネントを組み合わせた実用的な記事の書き方を示す。
 * ソースは frontmatter なしの本文（ガイドが frontmatter を補う）。
 */

export type CookbookSample = {
  id: string;
  title: string;
  description: string;
  source: string;
};

export const COOKBOOK_SAMPLES: CookbookSample[] = [
  {
    id: 'getting-started-article',
    title: '手順を説明する記事',
    description:
      'セクション見出し・本文・番号付きリスト・注意ボックスを組み合わせた、もっとも基本的な記事の形です。',
    source: [
      '<--- Section --->',
      'title:セットアップ手順',
      'number:1',
      'anchor:setup',
      'children:',
      ' - lead',
      ' - steps',
      ' - warn',
      '<--- !Section --->',
      '<--- Text --->',
      'id:lead',
      'text:',
      ' - 以下の順に進めてください。',
      '<--- !Text --->',
      '<--- List --->',
      'id:steps',
      'variant:ordered',
      'text:',
      ' - リポジトリを clone する',
      ' - 依存をインストールする',
      ' - 開発サーバーを起動する',
      '<--- !List --->',
      '<--- Callout --->',
      'id:warn',
      'type:warning',
      'text:',
      ' - Node.js のバージョンに注意してください。',
      '<--- !Callout --->',
    ].join('\n'),
  },
  {
    id: 'install-tabs',
    title: 'インストール手順をタブで出し分ける',
    description:
      'Tabs と Tab を入れ子にし、各 Tab の中に CodeBlock を入れて、パッケージマネージャごとのコマンドを切り替えます。',
    source: [
      '<--- Tabs --->',
      'children:',
      ' - tab-npm',
      ' - tab-yarn',
      ' - tab-pnpm',
      '<--- !Tabs --->',
      '<--- Tab --->',
      'id:tab-npm',
      'label:npm',
      'children:',
      ' - code-npm',
      '<--- !Tab --->',
      '<--- CodeBlock --->',
      'id:code-npm',
      'language:bash',
      'text:',
      'npm install',
      'npm run dev',
      '<--- !CodeBlock --->',
      '<--- Tab --->',
      'id:tab-yarn',
      'label:yarn',
      'children:',
      ' - code-yarn',
      '<--- !Tab --->',
      '<--- CodeBlock --->',
      'id:code-yarn',
      'language:bash',
      'text:',
      'yarn',
      'yarn dev',
      '<--- !CodeBlock --->',
      '<--- Tab --->',
      'id:tab-pnpm',
      'label:pnpm',
      'children:',
      ' - code-pnpm',
      '<--- !Tab --->',
      '<--- CodeBlock --->',
      'id:code-pnpm',
      'language:bash',
      'text:',
      'pnpm install',
      'pnpm dev',
      '<--- !CodeBlock --->',
    ].join('\n'),
  },
  {
    id: 'feature-grid',
    title: '機能紹介をカードで並べる',
    description: 'Grid の中に Card を複数入れて、機能や選択肢を横並びのカードで見せます。',
    source: [
      '<--- Grid --->',
      'columns:3',
      'children:',
      ' - f1',
      ' - f2',
      ' - f3',
      '<--- !Grid --->',
      '<--- Card --->',
      'id:f1',
      'title:高速',
      'text:',
      ' - 自前パーサで軽快に動作します。',
      'color:primary.main',
      'badge:速度',
      '<--- !Card --->',
      '<--- Card --->',
      'id:f2',
      'title:安全',
      'text:',
      ' - ホワイトリスト方式で安全に描画します。',
      'color:success.main',
      'badge:安全',
      '<--- !Card --->',
      '<--- Card --->',
      'id:f3',
      'title:拡張しやすい',
      'text:',
      ' - registry にコンポーネントを追加するだけ。',
      'color:warning.main',
      'badge:拡張',
      '<--- !Card --->',
    ].join('\n'),
  },
  {
    id: 'faq-tips',
    title: 'FAQ を開閉式でまとめる',
    description: 'Section の中に複数の Tip（開閉式）を入れて、質問と回答をコンパクトにまとめます。',
    source: [
      '<--- Section --->',
      'title:よくある質問',
      'number:1',
      'anchor:faq',
      'children:',
      ' - q1',
      ' - q2',
      '<--- !Section --->',
      '<--- Tip --->',
      'id:q1',
      'title:knz と Markdown の違いは？',
      'text:',
      ' - Markdown 風の本文に独自コンポーネントを埋め込める点が違います。',
      '<--- !Tip --->',
      '<--- Tip --->',
      'id:q2',
      'title:画像は使えますか？',
      'text:',
      ' - 現状はアップロード機能がないため対象外です。',
      '<--- !Tip --->',
    ].join('\n'),
  },
  {
    id: 'article-link-cards',
    title: '関連記事へのリンクカードを並べる',
    description:
      'ArticleLinkCard は articleId で指定した記事へのリンクカードです。タイトル・著者・公開日は記事から動的に取得されます。説明は text で上書きでき、未指定なら記事の description を表示します。Grid に並べると「関連記事」欄のように見せられます。section に遷移先記事の Section の anchor を指定すると、その記事の該当セクションへ飛ばせます（そのときは説明を text で書くのがおすすめ）。',
    source: [
      '<--- Grid --->',
      'columns:2',
      'children:',
      ' - link-1',
      ' - link-2',
      '<--- !Grid --->',
      '<--- ArticleLinkCard --->',
      'id:link-1',
      'articleId:a-001',
      '<--- !ArticleLinkCard --->',
      '<--- ArticleLinkCard --->',
      'id:link-2',
      'articleId:a-002',
      'section:overview',
      'text:概要セクションへ直接ジャンプします',
      'color:success.main',
      '<--- !ArticleLinkCard --->',
    ].join('\n'),
  },
  {
    id: 'prose-article',
    title: '読み物記事（明朝・新書風）',
    description:
      'frontmatter に prose: true と title / subtitle / breadcrumb を指定すると、本文が明朝体の紙面（devdocs/thoughts-ai と同等）で描画されます。Chapter・ProseText・ProseQuote・ProseHeading・ProseList・ProseTable を組み合わせて記事を構成します。完成例は .kiro/docs/thoughts_ai.knz を参照。',
    source: [
      '---',
      'title: 生成AIは道具か、それとも自分自身か',
      'author:',
      ' - preview',
      'prose: true',
      'subtitle: 認知機能の外部化についての考察',
      'breadcrumb: Dev Insights',
      '---',
      '<--- Chapter --->',
      'id:ch-1',
      'label:第一章',
      'title:文字、計算機、そして生成AI',
      'children:',
      ' - p1',
      ' - tbl1',
      ' - q1',
      ' - h1',
      ' - list1',
      '<--- !Chapter --->',
      '<--- ProseText --->',
      'id:p1',
      'text:',
      ' - 人類が最初に認知を外部へ預けたのは、文字の発明においてだった。**境界はグラデーションになる。**',
      '<--- !ProseText --->',
      '<--- ProseTable --->',
      'id:tbl1',
      'headers:',
      ' - 技術|外部化した認知機能',
      'text:',
      ' - 文字|記憶',
      ' - 計算機|計算',
      '<--- !ProseTable --->',
      '<--- ProseQuote --->',
      'id:q1',
      'text:',
      ' - 「私の発見したのは、記憶と知恵の秘訣なのですから」',
      '<--- !ProseQuote --->',
      '<--- ProseHeading --->',
      'id:h1',
      'title:小見出し',
      '<--- !ProseHeading --->',
      '<--- ProseList --->',
      'id:list1',
      'text:',
      ' - 箇条書きの項目1',
      ' - 箇条書きの項目2',
      '<--- !ProseList --->',
    ].join('\n'),
  },
  {
    id: 'full-article',
    title: '目次つきの記事',
    description:
      'Toc と複数の Section（anchor + title）を組み合わせると、記事公開ページで本文右側にスクロール連動の目次が表示され、読みやすい記事になります。',
    source: [
      '<--- Toc --->',
      '<--- !Toc --->',
      '<--- Section --->',
      'title:概要',
      'number:1',
      'anchor:s-overview',
      'children:',
      ' - o1',
      '<--- !Section --->',
      '<--- Text --->',
      'id:o1',
      'text:',
      ' - この記事の概要です。',
      '<--- !Text --->',
      '<--- Section --->',
      'title:詳細',
      'number:2',
      'anchor:s-detail',
      'children:',
      ' - d1',
      '<--- !Section --->',
      '<--- Text --->',
      'id:d1',
      'text:',
      ' - 詳細な説明です。目次のリンクから移動できます。',
      '<--- !Text --->',
    ].join('\n'),
  },
];
